import { Plus, X } from "lucide-react";
import { OIDC_SCOPES } from "@platform/core";
import { Button, Checkbox, Field, IconButton, Input, Label, NativeSelect } from "@platform/ui";
import { useT } from "../../i18n";

export interface ClientFormValue {
  name: string;
  type: "confidential" | "public";
  redirectUris: string[];
  postLogoutRedirectUris: string[];
  allowedScopes: string[];
  firstParty: boolean;
  backchannelLogoutUri: string;
  ownerTeam: string;
  description: string;
}

export const EMPTY_CLIENT: ClientFormValue = {
  name: "",
  type: "confidential",
  redirectUris: [""],
  postLogoutRedirectUris: [],
  allowedScopes: ["openid", "profile", "email"],
  firstParty: false,
  backchannelLogoutUri: "",
  ownerTeam: "",
  description: "",
};

function UriList({
  label,
  values,
  onChange,
  testId,
}: {
  label: string;
  values: string[];
  onChange: (v: string[]) => void;
  testId: string;
}) {
  const { t } = useT();
  return (
    <div className="mb-4">
      <Label>{label}</Label>
      <div className="space-y-2">
        {values.map((v, i) => (
          <div key={i} className="flex gap-2">
            <Input
              aria-label={`${label} ${i + 1}`}
              data-testid={`${testId}-${i}`}
              type="url"
              placeholder="https://app.example.com/callback"
              value={v}
              onChange={(e) => onChange(values.map((x, j) => (j === i ? e.target.value : x)))}
            />
            <IconButton label={t("common.remove")} onClick={() => onChange(values.filter((_, j) => j !== i))}>
              <X className="size-4" aria-hidden />
            </IconButton>
          </div>
        ))}
      </div>
      <Button variant="text" size="sm" className="mt-1" onClick={() => onChange([...values, ""])}>
        <Plus className="size-4" aria-hidden />
        {t("clients.addUri")}
      </Button>
    </div>
  );
}

export function ClientForm({
  value,
  onChange,
  isNew,
  fieldErrors,
}: {
  value: ClientFormValue;
  onChange: (v: ClientFormValue) => void;
  isNew: boolean;
  fieldErrors?: Record<string, string>;
}) {
  const { t } = useT();
  const err = (k: string) =>
    fieldErrors?.[k] ?? Object.entries(fieldErrors ?? {}).find(([p]) => p.startsWith(`${k}.`))?.[1];
  return (
    <div>
      <Field label={t("clients.name")} required error={err("name")}>
        {(p) => (
          <Input {...p} value={value.name} onChange={(e) => onChange({ ...value, name: e.target.value })} />
        )}
      </Field>
      {isNew && (
        <Field label={t("clients.col.type")}>
          {(p) => (
            <NativeSelect
              {...p}
              value={value.type}
              onChange={(e) => onChange({ ...value, type: e.target.value as ClientFormValue["type"] })}
            >
              <option value="confidential">{t("clients.type.confidential")}</option>
              <option value="public">{t("clients.type.public")}</option>
            </NativeSelect>
          )}
        </Field>
      )}
      <UriList
        label={t("clients.redirectUris")}
        values={value.redirectUris}
        onChange={(v) => onChange({ ...value, redirectUris: v })}
        testId="redirect-uri"
      />
      {err("redirectUris") && (
        <p className="-mt-3 mb-3 text-xs text-danger" role="alert">
          {err("redirectUris")}
        </p>
      )}
      <UriList
        label={t("clients.postLogoutUris")}
        values={value.postLogoutRedirectUris}
        onChange={(v) => onChange({ ...value, postLogoutRedirectUris: v })}
        testId="post-logout-uri"
      />
      <fieldset className="mb-4">
        <legend className="mb-2 text-sm font-medium">{t("clients.scopes")}</legend>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {OIDC_SCOPES.map((s) => (
            <Checkbox
              key={s}
              label={<code className="text-xs">{s}</code>}
              checked={value.allowedScopes.includes(s)}
              disabled={s === "openid"}
              onChange={(e) =>
                onChange({
                  ...value,
                  allowedScopes: e.target.checked
                    ? [...value.allowedScopes, s]
                    : value.allowedScopes.filter((x) => x !== s),
                })
              }
            />
          ))}
        </div>
      </fieldset>
      <Checkbox
        className="mb-4"
        label={`${t("clients.firstParty")} — ${t("clients.firstPartyHint")}`}
        checked={value.firstParty}
        onChange={(e) => onChange({ ...value, firstParty: e.target.checked })}
      />
      <Field label={t("clients.backchannel")} error={err("backchannelLogoutUri")}>
        {(p) => (
          <Input
            {...p}
            type="url"
            value={value.backchannelLogoutUri}
            onChange={(e) => onChange({ ...value, backchannelLogoutUri: e.target.value })}
          />
        )}
      </Field>
      <div className="grid gap-x-4 sm:grid-cols-2">
        <Field label={t("clients.ownerTeam")}>
          {(p) => (
            <Input
              {...p}
              value={value.ownerTeam}
              onChange={(e) => onChange({ ...value, ownerTeam: e.target.value })}
            />
          )}
        </Field>
        <Field label={t("clients.description")}>
          {(p) => (
            <Input
              {...p}
              value={value.description}
              onChange={(e) => onChange({ ...value, description: e.target.value })}
            />
          )}
        </Field>
      </div>
    </div>
  );
}

export type Scope = (typeof OIDC_SCOPES)[number];

/** Narrow free strings to the supported scopes (always including openid). */
export function toScopes(list: string[]): Scope[] {
  return [...new Set(["openid", ...list])].filter((s): s is Scope =>
    (OIDC_SCOPES as readonly string[]).includes(s),
  );
}

export function cleanUris(list: string[]): string[] {
  return list.map((s) => s.trim()).filter(Boolean);
}
